import classNames from 'classnames'
import React, { ReactNode, useMemo } from 'react'
import alpha from 'color-alpha'

import { ConfigConsumer } from 'antd/lib/config-provider'
import type { TagProps as AntdTagProps } from 'antd/lib/tag'
import AntdTag from 'antd/lib/tag'
import type { CSSProperties } from 'react'
import Ellipsis from '../ellipsis'

import './label.less'

export enum LabelColors {
  cyan = 'cyan',
  green = 'green',
  iris = 'iris',
  blue = 'blue',
  lavender = 'lavender',
  orange = 'orange',
  golden = 'golden',
  red = 'red',
  grey = 'grey',
}

const colors = {
  [LabelColors.cyan]: '#1e9493',
  [LabelColors.green]: '#07c790',
  [LabelColors.iris]: '#5e47d3',
  [LabelColors.blue]: '#126bfb',
  [LabelColors.lavender]: '#945fb9',
  [LabelColors.orange]: '#ed7b20',
  [LabelColors.golden]: '#de8a02',
  [LabelColors.red]: '#d96156',
  [LabelColors.grey]: '#5e6e90',
}

export interface LabelProps extends AntdTagProps {
  /**
   * 类型
   */
  type?: 'dot' | 'filled' | 'outline' | 'rounded'
  /**
   * 颜色
   */
  color?: LabelColors | string
  /**
   * 文字是否同色
   */
  textColorSame?: boolean
  /**
   * 图标
   */
  icon?: ReactNode
  /**
   * 图标位置
   */
  iconPosition?: 'prefix' | 'suffix'
  /**
   * 省略配置
   */
  ellipsisOptions?: {
    Popover?: boolean
    emptyText?: string
    widthLimit?: number | string
    lines?: number
    [key: string]: unknown
  }
}

/**
 * 计算标签样式
 *
 * @param type
 * @param preset
 * @param textColorSame
 */
const computeTagStyle = (
  type: LabelProps['type'],
  color?: string,
  textColorSame?: boolean
): CSSProperties | undefined => {
  if (!color) {
    return
  }

  switch (type) {
    case 'dot':
      if (textColorSame) {
        return {
          color,
        }
      }

      return
    case 'filled':
      return {
        color: '#fff',
        backgroundColor: color,
      }
    case 'rounded':
      return {
        color,
        backgroundColor: alpha(color, 0.1),
      }
    case 'outline':
      return {
        color,
        borderColor: alpha(color, 0.4),
        backgroundColor: alpha(color, 0.1),
      }
  }
}

export const Label = ({
  className,
  children,
  type,
  color,
  icon,
  style,
  textColorSame = icon != null,
  iconPosition = 'prefix',
  ellipsisOptions,
  ...props
}: LabelProps) => {
  const normalizeType = useMemo(() => {
    if (icon) {
      return 'dot'
    }

    return type
  }, [icon, type])
  const computedColor = useMemo(() => colors[color as LabelColors] || color, [color])
  const tagStyle = useMemo(
    () => computeTagStyle(normalizeType, computedColor, textColorSame),
    [normalizeType, computedColor, textColorSame]
  )
  const hasIcon = useMemo(() => icon != null, [icon])

  return (
    <ConfigConsumer>
      {({ getPrefixCls }) => {
        const prefixCls = getPrefixCls('label')
        const displayEllipsisOptions = (() => {
          const options = {
            ...ellipsisOptions,
          }
          const iconElement = (
            <span className={`${prefixCls}-icon`} style={{ color: computedColor }}>
              {icon}
            </span>
          )

          if (iconPosition === 'prefix') {
            options.prefix = iconElement
          }

          if (iconPosition === 'suffix') {
            options.suffix = iconElement
          }

          if (options.Popover === true) {
            options.content = children
          } else {
            options.title = children
          }

          return options
        })()

        return (
          <AntdTag
            {...props}
            className={classNames(className, prefixCls, {
              [`${prefixCls}-dot`]: normalizeType === 'dot',
              [`${prefixCls}-outline`]: normalizeType === 'outline',
              [`${prefixCls}-filled`]: normalizeType === 'filled',
              [`${prefixCls}-rounded`]: normalizeType === 'rounded',
            })}
            style={{
              ...style,
              ...tagStyle,
            }}
          >
            {normalizeType === 'dot' && icon == null && (
              <span
                className={`${prefixCls}-dot-symbol`}
                style={{
                  backgroundColor: computedColor,
                }}
              />
            )}
            {hasIcon ? <Ellipsis {...displayEllipsisOptions}>{children}</Ellipsis> : children}
          </AntdTag>
        )
      }}
    </ConfigConsumer>
  )
}

Label.CheckableLabel = AntdTag.CheckableTag

export default Label
